import { useNavigate } from "react-router-dom";
import { Settings, ShieldCheck, LogOut, ChevronDown, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";

export const getInitials = (name = "") => {
  const clean = name.replace(/[-_]/g, " ").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (clean.slice(0, 2) || "U").toUpperCase();
};

export const Avatar = ({ user, size = "h-9 w-9" }) => {
  const src = user?.avatar_url || user?.picture;
  if (src) {
    return <img src={src} alt="Your profile" data-testid="account-avatar-img" className={`${size} shrink-0 rounded-full object-cover ring-2 ring-[#4A7c59]`} />;
  }
  return (
    <div data-testid="account-avatar-initials" className={`${size} flex shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold text-[#4A7c59] ring-2 ring-[#4A7c59]`}>
      {getInitials(user?.display_name)}
    </div>
  );
};

export const AccountMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="account-menu-trigger" className="flex items-center gap-2 rounded-full border border-[#4A7c59]/25 bg-white py-1 pl-1 pr-3 text-sm font-medium text-[#2C363F] transition-colors duration-200 hover:bg-[#4A7c59]/5">
          <Avatar user={user} />
          <span className="max-w-[120px] truncate">{user.display_name}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-2xl" data-testid="account-menu-content">
        <DropdownMenuLabel className="truncate">{user.email || user.display_name}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="account-menu-settings" onClick={() => navigate("/app/settings")} className="cursor-pointer gap-2">
          <Settings className="h-4 w-4" /> Account Settings
        </DropdownMenuItem>
        {user.manages_centres && (
          <DropdownMenuItem data-testid="account-menu-mycentre" onClick={() => navigate("/app/my-centre")} className="cursor-pointer gap-2">
            <Building2 className="h-4 w-4" /> My Centre
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid="account-menu-privacy" onClick={() => navigate("/app/settings?tab=privacy")} className="cursor-pointer gap-2">
          <ShieldCheck className="h-4 w-4" /> Privacy &amp; Data
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="account-menu-signout" onClick={() => { logout(); navigate("/"); }} className="cursor-pointer gap-2 text-[#8a2f22] focus:text-[#8a2f22]">
          <LogOut className="h-4 w-4" /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
